import * as Cesium from "cesium";

const STATE_KEY = "__RB_STATE__";
const RB_TAG = "__RB_VIEW_BB__";
const __textCanvasCache = new Map();

const MAX_TEXT_CANVAS_CACHE = 1000; // limit text canvas cache size
const WORK_YIELD_INTERVAL = 2000; // yield after this many label attempts

export async function toggleLabel({
  viewer,
  dataSource = null,
  visible = true,
  show = true,
  keys = [],
  textSize = 13,
  textColor = "white",
  maxBillboardsPerChunk = 400,
  enableViewCulling = true,
  autoAttach = true,
  debounceMs = 1000,
  _internal = false,
  reset = false,
  clearTextCache = false,
  removeAll = false,
  offset = 2,
} = {}) {
  const createTextCanvas = (text, { textColor = "white", textSize = 13 } = {}) => {
    const key = `${text}|${textSize}|${textColor}`;
    const cached = __textCanvasCache.get(key);
    if (cached) return cached;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pad = 2;
    const font = `${textSize}px sans-serif`;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { alpha: true });

    ctx.font = font;
    ctx.textBaseline = "alphabetic";
    const w = Math.ceil(ctx.measureText(text).width) + pad * 2;
    const h = Math.ceil(textSize + pad * 2);

    canvas.width = Math.max(1, Math.ceil(w * dpr));
    canvas.height = Math.max(1, Math.ceil(h * dpr));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = font;
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = textColor;

    const x = pad;
    const y = pad + textSize;
    ctx.fillText(text, x, y);

    // simple cache size limit
    if (__textCanvasCache.size >= MAX_TEXT_CANVAS_CACHE) {
      __textCanvasCache.clear();
    }

    __textCanvasCache.set(key, canvas);
    return canvas;
  };

  function destroyBillboardLabels(viewer, { clearTextCache = false } = {}) {
    if (!viewer) return;

    const st = viewer[STATE_KEY];
    try {
      if (st?.listener) viewer.camera.changed.removeEventListener(st.listener);
    } catch {}
    try {
      if (st?.timer) clearTimeout(st.timer);
    } catch {}

    // remove visibility sync listeners
    try {
      const scene = viewer.scene;
      if (st?.visListenersByDs && scene?.postRender) {
        for (const [, fn] of st.visListenersByDs.entries()) {
          try {
            scene.postRender.removeEventListener(fn);
          } catch {}
        }
      }
    } catch {}

    try {
      const prims = viewer.scene?.primitives;
      if (prims) {
        for (let i = prims.length - 1; i >= 0; i--) {
          const p = prims.get(i);
          if (p && p._rbTag === RB_TAG) prims.remove(p);
        }
      }
    } catch {}

    try {
      const all = viewer.entities?.values || [];
      for (const ent of all) {
        if (Array.isArray(ent.billboardRef)) ent.billboardRef.length = 0;
        else if (ent.billboardRef) ent.billboardRef = [];
      }
    } catch {}

    if (clearTextCache) {
      try {
        __textCanvasCache.clear();
      } catch {}
    }

    viewer[STATE_KEY] = {
      timer: null,
      appliedKey: "",
      queuedKey: "",
      collection: null,
      listener: null,
      baseOpts: null,
      collectionsByDs: new Map(),
      baseOptsByDs: new Map(),
      visListenersByDs: new Map(),
      lastVisByDs: new Map(),
    };
  }

  /* ----- removeAll handling ----- */
  if (removeAll) {
    if (dataSource) {
      const scene = viewer.scene;
      const prims = scene?.primitives;
      const dsEntities = dataSource.entities?.values || [];
      const dsKey = dataSource || "__RB_GLOBAL__";

      if (prims) {
        for (let i = prims.length - 1; i >= 0; i--) {
          const p = prims.get(i);
          if (p && p._rbTag === RB_TAG && p._rbDs === dataSource) {
            prims.remove(p);
          }
        }
      }

      for (let i = 0; i < dsEntities.length; i++) {
        const e = dsEntities[i];
        if (Array.isArray(e.billboardRef)) e.billboardRef.length = 0;
        else e.billboardRef = [];
      }

      const st = viewer[STATE_KEY];
      if (st) {
        // remove visibility sync for this ds
        try {
          const fn = st.visListenersByDs?.get(dsKey);
          if (fn && scene?.postRender) scene.postRender.removeEventListener(fn);
        } catch {}
        if (st.visListenersByDs) st.visListenersByDs.delete(dsKey);
        if (st.lastVisByDs) st.lastVisByDs.delete(dsKey);

        if (st.collectionsByDs) st.collectionsByDs.delete(dsKey);
        if (st.baseOptsByDs) st.baseOptsByDs.delete(dsKey);

        let anyLeft = false;
        if (prims) {
          for (let i = 0; i < prims.length; i++) {
            const p = prims.get(i);
            if (p && p._rbTag === RB_TAG) {
              anyLeft = true;
              break;
            }
          }
        }
        if (!anyLeft && st.listener) {
          try {
            viewer.camera.changed.removeEventListener(st.listener);
          } catch {}
          st.listener = null;
          st.baseOpts = null;
          st.baseOptsByDs = new Map();
          st.appliedKey = "";
          st.queuedKey = "";
        }
      }

      if (clearTextCache) {
        try {
          __textCanvasCache.clear();
        } catch {}
      }

      if (scene?.requestRenderMode) scene.requestRender();
      return null;
    }

    destroyBillboardLabels(viewer, { clearTextCache });
    return null;
  }

  /* ----- HARD RESET ----- */
  if (reset) {
    const st = viewer[STATE_KEY];
    try {
      if (st?.listener) viewer.camera.changed.removeEventListener(st.listener);
    } catch {}
    try {
      if (st?.timer) clearTimeout(st.timer);
    } catch {}

    // remove visibility sync listeners
    try {
      const scene = viewer.scene;
      if (st?.visListenersByDs && scene?.postRender) {
        for (const [, fn] of st.visListenersByDs.entries()) {
          try {
            scene.postRender.removeEventListener(fn);
          } catch {}
        }
      }
    } catch {}

    const prims = viewer.scene.primitives;
    for (let i = prims.length - 1; i >= 0; i--) {
      const p = prims.get(i);
      if (p && p._rbTag === RB_TAG) prims.remove(p);
    }
    viewer[STATE_KEY] = {
      timer: null,
      appliedKey: "",
      queuedKey: "",
      collection: null,
      listener: null,
      baseOpts: null,
      collectionsByDs: new Map(),
      baseOptsByDs: new Map(),
      visListenersByDs: new Map(),
      lastVisByDs: new Map(),
    };
    if (clearTextCache) __textCanvasCache.clear();
  }

  /* ----- Per-viewer state ----- */
  if (!viewer[STATE_KEY]) {
    viewer[STATE_KEY] = {
      timer: null,
      appliedKey: "",
      queuedKey: "",
      collection: null,
      listener: null,
      baseOpts: null,
      collectionsByDs: new Map(),
      baseOptsByDs: new Map(),
      visListenersByDs: new Map(),
      lastVisByDs: new Map(),
    };
  }
  const state = viewer[STATE_KEY];
  if (!state.collectionsByDs) state.collectionsByDs = new Map();
  if (!state.baseOptsByDs) state.baseOptsByDs = new Map();
  if (!state.visListenersByDs) state.visListenersByDs = new Map();
  if (!state.lastVisByDs) state.lastVisByDs = new Map();

  /* ----- Resolve entities only from dataSource ----- */
  let entities = [];
  if (dataSource && dataSource.entities && Array.isArray(dataSource.entities.values)) {
    entities = dataSource.entities.values;
  }

  /* ----- Helpers ----- */
  const idle = () =>
    new Promise((res) => {
      if (typeof window !== "undefined" && window.requestIdleCallback) {
        window.requestIdleCallback(res);
      } else {
        setTimeout(res, 0);
      }
    });

  const getNow = () => {
    if (typeof performance !== "undefined" && performance.now) return performance.now();
    return Date.now();
  };

  const computeZoomLevel = () => {
    const scene = viewer.scene;
    const cam = viewer.camera;
    const w = scene.canvas.clientWidth;
    const h = scene.canvas.clientHeight;
    const y = h / 2;

    const left = cam.pickEllipsoid(new Cesium.Cartesian2(0, y), scene.globe.ellipsoid);
    const right = cam.pickEllipsoid(new Cesium.Cartesian2(w, y), scene.globe.ellipsoid);

    let mpp;
    if (left && right) {
      mpp = Cesium.Cartesian3.distance(left, right) / w;
    } else {
      const height = cam.positionCartographic?.height ?? 1;
      const fovy = cam.frustum.fovy || Cesium.Math.toRadians(60);
      mpp = (2 * height * Math.tan(fovy / 2)) / h;
    }

    const centerLat = cam.positionCartographic?.latitude ?? 0;
    const cosLat = Math.cos(centerLat);
    const R = new Cesium.WebMercatorProjection().ellipsoid.maximumRadius;
    return Math.log2((cosLat * 2 * Math.PI * R) / (mpp * 256));
  };

  const getViewRectangleStrict = () => {
    if (!enableViewCulling) return null;
    const scene = viewer.scene;
    const ellipsoid = scene.globe?.ellipsoid;
    if (viewer.camera.computeViewRectangle && ellipsoid) {
      const r = viewer.camera.computeViewRectangle(ellipsoid);
      if (r) return r;
    }
    const w = scene.canvas.clientWidth;
    const h = scene.canvas.clientHeight;
    const samples = [
      [0, 0],
      [w, 0],
      [w, h],
      [0, h],
      [w / 2, 0],
      [w, h / 2],
      [w / 2, h],
      [0, h / 2],
      [w / 2, h / 2],
    ];
    const cartos = [];
    for (const [sx, sy] of samples) {
      const c2 = new Cesium.Cartesian2(sx, sy);
      const ray = viewer.camera.getPickRay(c2);
      if (!ray) continue;
      const scene = viewer.scene;
      const globe = scene.globe;
      let hit = globe?.pick(ray, scene);
      if (!hit && ellipsoid) {
        const inter = Cesium.IntersectionTests.rayEllipsoid(ray, ellipsoid);
        if (inter) hit = Cesium.Ray.getPoint(ray, inter.start);
      }
      if (!hit) continue;
      const carto = Cesium.Cartographic.fromCartesian(hit, ellipsoid);
      if (carto) cartos.push(carto);
    }
    return cartos.length >= 3 ? Cesium.Rectangle.fromCartographicArray(cartos) : null;
  };

  const angleOnScreen = (pA, pB) => {
    const scene = viewer.scene;
    const a2 = Cesium.SceneTransforms.worldToWindowCoordinates(scene, pA);
    const b2 = Cesium.SceneTransforms.worldToWindowCoordinates(scene, pB);
    if (!a2 || !b2) return 0;

    const dx = b2.x - a2.x;
    const dy = a2.y - b2.y;

    let ang = Math.atan2(dy, dx);

    if (ang > Math.PI / 2) ang -= Math.PI;
    else if (ang < -Math.PI / 2) ang += Math.PI;

    return ang;
  };

  const makeKey = () => {
    const z = computeZoomLevel();
    const cam = viewer.camera;
    const pc = cam.positionCartographic || {
      longitude: 0,
      latitude: 0,
      height: 0,
    };

    const zStr = Number.isFinite(z) ? z.toPrecision(15) : "0";
    const hStr = (pc.height ?? 0).toPrecision(15);
    const latStr = (pc.latitude ?? 0).toPrecision(15);
    const lonStr = (pc.longitude ?? 0).toPrecision(15);

    const hd = cam.heading != null && Number.isFinite(cam.heading) ? cam.heading.toPrecision(15) : "0";
    const pt = cam.pitch != null && Number.isFinite(cam.pitch) ? cam.pitch.toPrecision(15) : "0";
    const rl = cam.roll != null && Number.isFinite(cam.roll) ? cam.roll.toPrecision(15) : "0";

    return `${zStr}|${hStr}|${latStr}|${lonStr}|${hd}|${pt}|${rl}`;
  };

  // FIX: honor entity/dataSource hidden states for labels
  const getShowValue = (v, time) => {
    if (v == null) return true;
    if (typeof v === "boolean") return v;
    if (typeof v.getValue === "function") return !!v.getValue(time);
    return true;
  };

  const isEntityVisibleNow = (entity, time) => {
    if (dataSource && dataSource.show === false) return false;

    const baseEntityShow = typeof entity?.isShowing === "boolean" ? entity.isShowing : getShowValue(entity?.show, time);
    if (!baseEntityShow) return false;

    if (entity?.polyline?.positions) return getShowValue(entity.polyline.show, time);
    if (entity?.polygon?.hierarchy) return getShowValue(entity.polygon.show, time);

    return true;
  };

  const ensureVisibilitySync = (dsKey, collection) => {
    if (!dataSource) return;
    if (state.visListenersByDs.has(dsKey)) return;

    const scene = viewer.scene;
    const throttleMs = 150;

    const last = {
      t: 0,
      dsShow: dataSource.show !== false,
      ent: new Map(),
    };
    state.lastVisByDs.set(dsKey, last);

    const fn = () => {
      const nowMs = getNow();
      if (nowMs - last.t < throttleMs) return;
      last.t = nowMs;

      const dsShow = dataSource.show !== false;
      collection.show = !!visible && dsShow;

      if (!dsShow) return;

      const nowTime = Cesium.JulianDate.now();
      const ents = dataSource.entities?.values || [];
      for (let i = 0; i < ents.length; i++) {
        const e = ents[i];
        const eid = e?.id != null ? String(e.id) : `__ent_${i}`;
        const v = !!visible && isEntityVisibleNow(e, nowTime);
        const prev = last.ent.get(eid);
        if (prev === v) continue;
        last.ent.set(eid, v);

        const refs = Array.isArray(e.billboardRef) ? e.billboardRef : null;
        if (!refs || refs.length === 0) continue;
        for (let j = 0; j < refs.length; j++) {
          const bb = refs[j];
          if (bb) bb.show = v;
        }
      }
    };

    scene.postRender.addEventListener(fn);
    state.visListenersByDs.set(dsKey, fn);
  };

  /* ----- Auto-attach listener (debounced) ----- */
  if (autoAttach && !_internal && dataSource && Array.isArray(entities) && entities.length > 0 && show !== false) {
    const dsKey = dataSource || "__RB_GLOBAL__";

    state.baseOptsByDs.set(dsKey, {
      viewer,
      dataSource,
      visible,
      keys,
      textSize,
      textColor,
      maxBillboardsPerChunk,
      enableViewCulling,
      autoAttach,
      debounceMs,
      show,
      clearTextCache,
      offset,
    });

    if (!state.listener) {
      const onCameraChanged = () => {
        const k = makeKey();
        if (k === state.queuedKey) return;
        state.queuedKey = k;
        if (state.timer) clearTimeout(state.timer);
        state.timer = setTimeout(() => {
          if (state.queuedKey !== state.appliedKey) {
            const optsByDs = state.baseOptsByDs;
            const colByDs = state.collectionsByDs;
            if (optsByDs && colByDs) {
              for (const [, opts] of optsByDs.entries()) {
                toggleLabel({
                  ...opts,
                  _internal: true,
                });
              }
            }
          }
          state.timer = null;
        }, debounceMs);
      };
      viewer.camera.changed.addEventListener(onCameraChanged);
      state.listener = onCameraChanged;
    }
  }

  const scene = viewer.scene;

  if (dataSource && show === false) {
    const dsEntities = dataSource.entities?.values || [];
    const prims = scene.primitives;
    const dsKey = dataSource || "__RB_GLOBAL__";

    for (let i = prims.length - 1; i >= 0; i--) {
      const p = prims.get(i);
      if (p && p._rbTag === RB_TAG && p._rbDs === dataSource) {
        prims.remove(p);
      }
    }

    for (let i = 0; i < dsEntities.length; i++) {
      const e = dsEntities[i];
      if (Array.isArray(e.billboardRef)) e.billboardRef.length = 0;
      else e.billboardRef = [];
    }

    // remove visibility sync for this ds
    try {
      const fn = state.visListenersByDs?.get(dsKey);
      if (fn && scene?.postRender) scene.postRender.removeEventListener(fn);
    } catch {}
    if (state.visListenersByDs) state.visListenersByDs.delete(dsKey);
    if (state.lastVisByDs) state.lastVisByDs.delete(dsKey);

    if (state.collectionsByDs) state.collectionsByDs.delete(dsKey);
    if (state.baseOptsByDs) state.baseOptsByDs.delete(dsKey);

    let anyLeft = false;
    for (let i = 0; i < prims.length; i++) {
      const p = prims.get(i);
      if (p && p._rbTag === RB_TAG) {
        anyLeft = true;
        break;
      }
    }
    if (!anyLeft && state.listener) {
      try {
        viewer.camera.changed.removeEventListener(state.listener);
      } catch {}
      state.listener = null;
      state.baseOpts = null;
      state.baseOptsByDs = new Map();
      state.appliedKey = "";
      state.queuedKey = "";
    }

    if (scene.requestRenderMode) scene.requestRender();
    return null;
  }

  /* ================= BUILD ================= */
  const prims = scene.primitives;
  const dsKey = dataSource || "__RB_GLOBAL__";

  let collection = state.collectionsByDs.get(dsKey);
  if (!collection || !prims.contains(collection)) {
    collection = prims.add(new Cesium.BillboardCollection());
  }

  collection._rbTag = RB_TAG;
  if (dataSource) collection._rbDs = dataSource;
  else collection._rbDs = null;

  // FIX: datasource hidden => collection hidden
  const dsVisible = dataSource ? dataSource.show !== false : true;
  collection.show = !!visible && dsVisible;

  collection.removeAll();

  state.collection = collection;
  state.collectionsByDs.set(dsKey, collection);

  // FIX: sync visibility even when no camera move (entity/dataSource hide)
  ensureVisibilitySync(dsKey, collection);

  const isCollectionDead = () => !collection || (typeof collection.isDestroyed === "function" && collection.isDestroyed());

  const labelPixelOffset = new Cesium.Cartesian2(0, offset);

  const safeAddBillboard = (opts) => {
    if (isCollectionDead()) return null;
    try {
      return collection.add(opts);
    } catch {
      return null;
    }
  };

  const now = Cesium.JulianDate.now();
  const globe = scene.globe;
  const ellipsoid = globe?.ellipsoid;
  const scratchC3 = new Cesium.Cartesian3();
  const scratchCarto = new Cesium.Cartographic();
  const scratchA = new Cesium.Cartographic();
  const scratchB = new Cesium.Cartographic();

  const STEP_METERS = 1;
  const MIN_SEP_PX = 150;

  const startTime = getNow();
  let lastYield = startTime;
  const shouldYield = () => getNow() - lastYield > 12; // about one frame

  const imageCache = new Map();
  const getImage = (text) => {
    if (!imageCache.has(text)) {
      const canvas = createTextCanvas(text, { textColor, textSize });
      imageCache.set(text, canvas);
    }
    return imageCache.get(text);
  };

  const isFiniteC3 = (c) => !!c && Number.isFinite(c.x) && Number.isFinite(c.y) && Number.isFinite(c.z);

  const findValueByKey = (obj, key) => {
    if (!obj || typeof obj !== "object") return null;
    if (typeof obj.getValue === "function") obj = obj.getValue(now);
    if (obj && Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
    for (const k in obj) {
      const v = obj[k];
      if (v && typeof v === "object") {
        const r = findValueByKey(v, key);
        if (r !== null && r !== undefined) return r;
      }
    }
    return null;
  };

  const viewRect = getViewRectangleStrict();
  if (enableViewCulling && !viewRect) {
    if (scene.requestRenderMode) scene.requestRender();
    return collection;
  }

  const zRaw = computeZoomLevel();
  const zoom = Number.isFinite(zRaw) ? zRaw : 0;
  const minSepSq = MIN_SEP_PX * MIN_SEP_PX;

  const globalScreenPts = [];
  const seedGlobalFromExisting = () => {
    const primsLocal = scene.primitives;
    const MAX_SEED = 500;
    let count = 0;
    for (let i = 0; i < primsLocal.length; i++) {
      const p = primsLocal.get(i);
      if (!p || p === collection || p._rbTag !== RB_TAG) continue;
      const len = typeof p.length === "number" ? p.length : 0;
      for (let j = 0; j < len; j++) {
        const bb = p.get(j);
        if (!bb || !bb.show || !bb.position) continue;
        const win = Cesium.SceneTransforms.worldToWindowCoordinates(scene, bb.position);
        if (!win) continue;
        globalScreenPts.push(new Cesium.Cartesian2(win.x, win.y));
        count++;
        if (count >= MAX_SEED) return;
      }
    }
  };
  seedGlobalFromExisting();

  const canPlaceGlobal = (cartesian) => {
    const win = Cesium.SceneTransforms.worldToWindowCoordinates(scene, cartesian);
    if (!win) return false;
    for (let i = 0; i < globalScreenPts.length; i++) {
      const p = globalScreenPts[i];
      const dx = win.x - p.x;
      const dy = win.y - p.y;
      if (dx * dx + dy * dy < minSepSq) return false;
    }
    globalScreenPts.push(new Cesium.Cartesian2(win.x, win.y));
    return true;
  };

  const screenPtsByEntity = new Map();
  const canPlacePerEntity = (cartesian, entityId) => {
    const win = Cesium.SceneTransforms.worldToWindowCoordinates(scene, cartesian);
    if (!win) return false;
    if (!screenPtsByEntity.has(entityId)) {
      screenPtsByEntity.set(entityId, []);
    }
    const pts = screenPtsByEntity.get(entityId);
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const dx = win.x - p.x;
      const dy = win.y - p.y;
      if (dx * dx + dy * dy < minSepSq) return false;
    }
    pts.push(new Cesium.Cartesian2(win.x, win.y));
    return true;
  };

  const sampleInsideView = (cartesian) => {
    if (!enableViewCulling || !viewRect) return true;
    const carto = Cesium.Cartographic.fromCartesian(cartesian, ellipsoid, scratchCarto);
    if (!carto) return false;
    return Cesium.Rectangle.contains(viewRect, carto);
  };

  const entityIntersectsView = (positions) => {
    if (!enableViewCulling || !viewRect) return true;
    let hasAny = false;
    const cartos = [];
    for (let i = 0; i < positions.length; i++) {
      const c = Cesium.Cartographic.fromCartesian(positions[i], ellipsoid);
      if (!c) continue;
      hasAny = true;
      if (Cesium.Rectangle.contains(viewRect, c)) return true;
      cartos.push(c);
    }
    if (!hasAny) return false;
    if (cartos.length < 2) return false;
    const entRect = Cesium.Rectangle.fromCartographicArray(cartos);
    return !!Cesium.Rectangle.intersection(viewRect, entRect);
  };

  const segmentTouchesView = (a, b) => {
    if (!enableViewCulling || !viewRect) return true;
    Cesium.Cartographic.fromCartesian(a, ellipsoid, scratchA);
    Cesium.Cartographic.fromCartesian(b, ellipsoid, scratchB);
    const west = Math.min(scratchA.longitude, scratchB.longitude);
    const east = Math.max(scratchA.longitude, scratchB.longitude);
    const south = Math.min(scratchA.latitude, scratchB.latitude);
    const north = Math.max(scratchA.latitude, scratchB.latitude);
    const segRect = new Cesium.Rectangle(west, south, east, north);
    return !!Cesium.Rectangle.intersection(viewRect, segRect);
  };

  const localAngleAt = (positions, i) => {
    const prev = i > 0 ? positions[i - 1] : null;
    const next = i < positions.length - 1 ? positions[i + 1] : null;
    if (prev && isFiniteC3(prev)) return angleOnScreen(prev, positions[i]);
    if (next && isFiniteC3(next)) return angleOnScreen(positions[i], next);
    return 0;
  };

  const placeOneInside = (positions, img, storeRef, placeFn) => {
    if (isCollectionDead()) return false;

    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (sampleInsideView(p) && placeFn(p)) {
        const ang = localAngleAt(positions, i);
        const bb = safeAddBillboard({
          position: Cesium.Cartesian3.clone(p),
          image: img,
          rotation: Number.isFinite(ang) ? ang : 0,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          alignedAxis: Cesium.Cartesian3.ZERO,
          show: visible,
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          pixelOffset: labelPixelOffset,
        });
        if (!bb) return false;
        storeRef.push(bb);
        return true;
      }
    }
    const DENSITY = 16;
    for (let i = 1; i < positions.length; i++) {
      const a = positions[i - 1];
      const b = positions[i];
      if (!isFiniteC3(a) || !isFiniteC3(b)) continue;
      if (!segmentTouchesView(a, b)) continue;
      for (let t = 1; t <= DENSITY; t++) {
        const f = t / (DENSITY + 1);
        const p = Cesium.Cartesian3.lerp(a, b, f, scratchC3);
        if (sampleInsideView(p) && placeFn(p)) {
          const ang = angleOnScreen(a, b);
          const bb = safeAddBillboard({
            position: Cesium.Cartesian3.clone(p),
            image: img,
            rotation: Number.isFinite(ang) ? ang : 0,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            alignedAxis: Cesium.Cartesian3.ZERO,
            show: visible,
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            pixelOffset: labelPixelOffset,
          });
          if (!bb) return false;
          storeRef.push(bb);
          return true;
        }
      }
    }
    return false;
  };

  const nowTime = Cesium.JulianDate.now();
  let built = 0;
  let work = 0;

  // FIX: datasource hidden => build nothing
  if (dataSource && dataSource.show === false) {
    if (scene.requestRenderMode) scene.requestRender();
    if (autoAttach && !_internal) {
      state.appliedKey = state.queuedKey = makeKey();
    } else if (autoAttach && _internal) {
      state.appliedKey = state.queuedKey;
    }
    return collection;
  }

  for (let i = 0; i < entities.length; i++) {
    if (shouldYield()) {
      if (scene.requestRenderMode) scene.requestRender();
      await idle();
      lastYield = getNow();
      if (isCollectionDead()) {
        return null;
      }
    }

    const e = entities[i];

    if (Array.isArray(e.billboardRef)) e.billboardRef.length = 0;
    else e.billboardRef = [];

    // FIX: entity hidden => skip (no labels for hidden entities)
    if (!isEntityVisibleNow(e, nowTime)) continue;

    let positions = null;

    if (e.polyline?.positions) {
      positions = e.polyline.positions.getValue(nowTime);
    } else if (e.polygon?.hierarchy) {
      const hier = e.polygon.hierarchy.getValue(nowTime);
      if (hier?.positions?.length) {
        positions = hier.positions.slice();
        if (!Cesium.Cartesian3.equals(positions[0], positions[positions.length - 1])) {
          positions.push(positions[0]);
        }
      }
    } else if (e.position) {
      const p = e.position.getValue ? e.position.getValue(nowTime) : e.position;
      if (isFiniteC3(p)) positions = [p];
    }

    if (!Array.isArray(positions) || positions.length === 0) continue;
    if (!entityIntersectsView(positions)) continue;

    let text = "";
    if (keys.length && e.properties) {
      const props = e.properties.getValue ? e.properties.getValue(nowTime) : e.properties;
      text = keys
        .map((k) => {
          const v = findValueByKey(props, k);
          return v !== null && v !== undefined ? String(v) : "-";
        })
        .join(" - ");
    }
    if (!text) continue;

    const img = getImage(text);

    const usePerEntity = zoom >= 20;
    const eid = e.id != null ? String(e.id) : `__ent_${i}`;
    if (usePerEntity && !screenPtsByEntity.has(eid)) {
      screenPtsByEntity.set(eid, []);
    }
    const placeFn = (pos) => (usePerEntity ? canPlacePerEntity(pos, eid) : canPlaceGlobal(pos));

    let placedAny = placeOneInside(positions, img, e.billboardRef, placeFn);

    if (positions.length === 1) {
      if (scene.requestRenderMode) scene.requestRender();
      continue;
    }

    let acc = 0;
    let last = positions[0];

    for (let j = 1; j < positions.length; j++) {
      const cur = positions[j];
      if (!isFiniteC3(last) || !isFiniteC3(cur)) {
        last = cur;
        continue;
      }

      let segLen = Cesium.Cartesian3.distance(last, cur);
      if (!Number.isFinite(segLen) || segLen <= 0) {
        last = cur;
        continue;
      }

      if (!segmentTouchesView(last, cur)) {
        acc = (acc + segLen) % STEP_METERS;
        last = cur;
        continue;
      }

      const total = acc + segLen;
      if (total >= STEP_METERS) {
        const n = Math.floor(total / STEP_METERS);
        const invSegLen = 1.0 / segLen;
        for (let stepIndex = 0; stepIndex < n; stepIndex++) {
          work++;
          if (work % WORK_YIELD_INTERVAL === 0 || shouldYield()) {
            if (scene.requestRenderMode) scene.requestRender();
            await idle();
            lastYield = getNow();
            if (isCollectionDead()) {
              return null;
            }
          }

          const distAlong = STEP_METERS - acc + STEP_METERS * stepIndex;
          const t = distAlong * invSegLen;
          if (!(t >= 0 && t <= 1)) continue;

          const pos = Cesium.Cartesian3.lerp(last, cur, t, scratchC3);
          if (sampleInsideView(pos) && placeFn(pos)) {
            const ang = angleOnScreen(last, cur);
            const bb = safeAddBillboard({
              position: Cesium.Cartesian3.clone(pos),
              image: img,
              rotation: Number.isFinite(ang) ? ang : 0,
              verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
              horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
              alignedAxis: Cesium.Cartesian3.ZERO,
              show: visible,
              heightReference: Cesium.HeightReference.NONE,
              disableDepthTestDistance: Number.POSITIVE_INFINITY,
              pixelOffset: labelPixelOffset,
            });
            if (!bb) return null;
            e.billboardRef.push(bb);
            placedAny = true;

            built++;
            if (built >= maxBillboardsPerChunk) {
              built = 0;
              if (scene.requestRenderMode) scene.requestRender();
              await idle();
              lastYield = getNow();
              if (isCollectionDead()) {
                return null;
              }
            }
          }
        }

        acc = total % STEP_METERS;
      } else {
        acc = total;
      }
      last = cur;
    }

    if (built >= maxBillboardsPerChunk) {
      built = 0;
      if (scene.requestRenderMode) scene.requestRender();
      await idle();
      lastYield = getNow();
      if (isCollectionDead()) {
        return null;
      }
    }
  }

  if (scene.requestRenderMode) scene.requestRender();

  if (autoAttach && !_internal) {
    state.appliedKey = state.queuedKey = makeKey();
  } else if (autoAttach && _internal) {
    state.appliedKey = state.queuedKey;
  }

  return collection;
}
